import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import type { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';

interface ChannelSelectorProps {
  channels: ReadDepartmentRecordsChannel[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  isFoia: boolean;
}

export const ChannelSelector = ({
  channels,
  selectedIdx,
  onSelect,
  isFoia,
}: ChannelSelectorProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (channels.length === 0) return null;

  const selected = channels[selectedIdx];
  const addr = selected?.mailing_instructions?.address;
  const fee = selected?.mailing_instructions?.fee_structure;
  const feeAmount = fee?.estimated_fee_cents
    ? (fee.estimated_fee_cents / 100).toFixed(2)
    : null;

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Selected channel display */}
      <button
        type="button"
        onClick={() => channels.length > 1 && setIsExpanded(!isExpanded)}
        className={`w-full p-4 bg-gray-50 text-left ${
          channels.length > 1 ? 'hover:bg-gray-100 cursor-pointer' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-600">
                Mailing Address
              </span>
              {channels.length > 1 && (
                <>
                  {isExpanded ? (
                    <ChevronUpIcon className="h-3.5 w-3.5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500" />
                  )}
                  <span className="text-xs text-blue-600">
                    {channels.length} options
                  </span>
                </>
              )}
            </div>
            {addr && (
              <div className="text-sm text-gray-900 space-y-0.5">
                <div>{addr.address_line1}</div>
                {addr.address_line2 && <div>{addr.address_line2}</div>}
                <div>
                  {[addr.city, addr.province_or_state, addr.postal_or_zip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
            )}
          </div>

          {/* Fee display */}
          {feeAmount && (
            <div className="text-right">
              <div className="text-xs text-gray-600 mb-0.5">
                {isFoia ? 'Optional Fee' : 'Cheque Amount'}
              </div>
              <div className="text-lg font-semibold text-gray-900">
                ${feeAmount}
              </div>
            </div>
          )}
        </div>

        {/* Payable to */}
        {selected?.mailing_instructions?.payable_to && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs">
            <span className="text-gray-600">Payable to:</span>
            <span className="font-medium text-gray-900">
              {selected.mailing_instructions.payable_to}
            </span>
          </div>
        )}
      </button>

      {/* Expanded channel list */}
      {isExpanded && channels.length > 1 && (
        <div className="p-3 border-t bg-white space-y-2">
          {channels.map((channel, idx) => {
            const channelAddr = channel.mailing_instructions?.address;
            const channelFee = channel.mailing_instructions?.fee_structure;
            const channelFeeAmount = channelFee?.estimated_fee_cents
              ? (channelFee.estimated_fee_cents / 100).toFixed(2)
              : null;
            const isSelected = selectedIdx === idx;

            return (
              <button
                key={channel.id || idx}
                type="button"
                onClick={() => {
                  onSelect(idx);
                  setIsExpanded(false);
                }}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  isSelected
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {channelAddr && (
                      <>
                        <div className="text-xs font-medium text-gray-900">
                          {channelAddr.address_line1}
                        </div>
                        {channelAddr.address_line2 && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            {channelAddr.address_line2}
                          </div>
                        )}
                        <div className="text-xs text-gray-600 mt-0.5">
                          {channelAddr.city}, {channelAddr.province_or_state}{' '}
                          {channelAddr.postal_or_zip}
                        </div>
                      </>
                    )}
                    {channel.title && (
                      <div className="text-xs text-gray-500 mt-1">
                        {channel.title}
                      </div>
                    )}
                  </div>
                  {channelFeeAmount && (
                    <div className="text-xs font-semibold text-gray-900">
                      ${channelFeeAmount}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
